import { alpha, Box, styled, Typography } from '@mui/material';
import { COMPARISON_CARD_SIZES } from '../constants';
import { StatisticSize } from '../types';

export const ComparisonCardValueTag = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'bgColor' && prop !== 'size',
})<{ bgColor: string; size?: StatisticSize }>(({
  theme,
  bgColor,
  size = 'md',
}) => {
  const sizeConfig = COMPARISON_CARD_SIZES[size] ?? COMPARISON_CARD_SIZES.md;
  return {
    display: 'inline-flex',
    alignItems: 'center',
    alignSelf: 'flex-start',
    justifyContent: 'center',
    gap: theme.spacing(0.5),
    backgroundColor: alpha(bgColor, theme.palette.mode === 'dark' ? 0.3 : 0.1),
    border: '1px solid',
    borderColor: bgColor,
    color: theme.palette.text.primary,
    fontWeight: 500,
    borderRadius: theme.spacing(1.5),
    padding: theme.spacing(0.25, 1),
    fontSize: sizeConfig.tagFontSize,
    lineHeight: 1.5,
  };
});

export const ComparisonCardHeaderWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'size',
})<{ size?: StatisticSize }>(({ theme, size = 'md' }) => {
  const sizeConfig = COMPARISON_CARD_SIZES[size] ?? COMPARISON_CARD_SIZES.md;
  return {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(sizeConfig.itemGap + 0.5),
    minHeight: sizeConfig.iconSize,
  };
});

export const ComparisonCardTitle = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'size',
})<{ size?: StatisticSize }>(({ size = 'md' }) => {
  const sizeConfig = COMPARISON_CARD_SIZES[size] ?? COMPARISON_CARD_SIZES.md;
  return {
    display: 'inline-flex',
    alignItems: 'center',
    fontSize: sizeConfig.titleFontSize,
    color: 'text.primary',
    lineHeight: 1.5,
  };
});

export const ComparisonCardTagTypography = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'size',
})<{ size?: StatisticSize }>(({ size = 'md' }) => {
  const sizeConfig = COMPARISON_CARD_SIZES[size] ?? COMPARISON_CARD_SIZES.md;
  return {
    fontSize: sizeConfig.tagFontSize,
  };
});

export const ComparisonCardMetricText = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'size',
})<{ size?: StatisticSize }>(({ size = 'md' }) => {
  const sizeConfig = COMPARISON_CARD_SIZES[size] ?? COMPARISON_CARD_SIZES.md;
  return {
    fontSize: sizeConfig.metricFontSize,
    fontWeight: 500,
    color: 'text.primary',
    lineHeight: 1.5,
  };
});

export const ComparisonCardLabel = styled(Typography, {
  shouldForwardProp: (prop) => !['size', 'color'].includes(prop as string),
})<{ size?: StatisticSize; color?: 'primary' | 'disabled' | 'error' }>(({
  theme,
  size = 'md',
  color = 'primary',
}) => {
  const sizeConfig = COMPARISON_CARD_SIZES[size] ?? COMPARISON_CARD_SIZES.md;
  const paletteColor =
    color === 'error'
      ? theme.palette.error.main
      : color === 'disabled'
        ? theme.palette.text.disabled
        : theme.palette.text.primary;
  return {
    fontSize: sizeConfig.labelFontSize,
    fontWeight: 600,
    lineHeight: 1.4,
    color: paletteColor,
  };
});

export const ComparisonCardMetricsRow = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'size',
})<{ size?: StatisticSize }>(({ size = 'md' }) => {
  const sizeConfig = COMPARISON_CARD_SIZES[size] ?? COMPARISON_CARD_SIZES.md;
  return {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: sizeConfig.metricGap,
  };
});

export const ComparisonCardContentInner = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'size',
})<{ size?: StatisticSize }>(({ size = 'md' }) => {
  const sizeConfig = COMPARISON_CARD_SIZES[size] ?? COMPARISON_CARD_SIZES.md;
  return {
    display: 'flex',
    flexDirection: 'column',
    gap: sizeConfig.itemGap,
  };
});
